import PropTypes from 'prop-types';
import React from 'react';

import {ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ReviewAction, ReviewHistoryItem} from 'notebook/reviews/ReviewConstants';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {formalTimeUTC} from 'util/DateUtil';

export interface ReviewHistoryContainerProps {
  reviewHistoryItems: Array<ReviewHistoryItem>;
  showReviewLifecycleColumns: boolean;
  showReviewCommentHistory: boolean;
  reviewProcessName?: string;
  reviewProcessId?: string | number;
}

interface ReviewHistoryItemProps {
  item: ReviewHistoryItem;
  beforeStatus: ReviewStatus | null | undefined;
  afterStatus: ReviewStatus | null | undefined;
  reviewStage: string;
  stageDefinition?: string;
  showReviewLifecycleColumns: boolean;
  showReviewCommentHistory: boolean;
}

function ReviewHistoryItemComponent({
  item,
  beforeStatus,
  afterStatus,
  reviewStage,
  stageDefinition,
  showReviewLifecycleColumns,
  showReviewCommentHistory,
}: ReviewHistoryItemProps): JSX.Element {
  // Use signature data directly from the review history item if available
  const signatureData = item.info?.signatureData;
  const reviewerInformation = {
    reviewerName: signatureData?.userFullName ?? item.user.name,
    reviewerEmail: signatureData?.userEmail ?? item.user.email,
    reviewerUserId: signatureData?.userId ?? item.user.id,
    timestamp: item.time,
    ipAddress: signatureData?.ipAddress,
    authProvider: signatureData?.authenticationMethod, // If null, we won't show the signature-auth-provider-badge
    signatureVersion: signatureData?.version,
    reviewHistoryUiVersion: signatureData?.reviewHistoryUiVersion,
  };

  return (
    <div className="review-history-item">
      <div className="review-item-details">
        <div className="review-item-columns">
          {/* Column 1: E-Signature */}
          <div className="review-item-column review-item-signature-column">
            <div className="review-item-label">Reviewer</div>
            <div className="review-item-signature-card">
              <div className="signature-user-name">User: {reviewerInformation.reviewerName}</div>
              {reviewerInformation.authProvider && (
                <div className="signature-auth-provider-badge">
                  <BenchlingIcon className="signature-auth-provider-icon" icon="esign" />
                  e-signed with {reviewerInformation.authProvider}
                </div>
              )}
              <div className="signature-user-id">User ID: {reviewerInformation.reviewerUserId}</div>
              <div className="signature-user-email">{reviewerInformation.reviewerEmail}</div>
              <div className="signature-timestamp">
                Signed: {formalTimeUTC(reviewerInformation.timestamp)}
              </div>
              <div className="signature-ip">IP Address: {reviewerInformation.ipAddress}</div>
            </div>
          </div>

          {showReviewLifecycleColumns ? (
            <>
              <div className="review-item-column">
                <div className="review-item-label">Stage</div>
                <div className="review-item-value">{reviewStage}</div>
              </div>
              <div className="review-item-column">
                <div className="review-item-label">Definition</div>
                <div className="review-item-value">{stageDefinition}</div>
              </div>
              <div className="review-item-column">
                <div className="review-item-label">Status Before</div>
                <div className="review-item-value">
                  {beforeStatus ? ReviewUtil.getStatusTextFromReview(beforeStatus) : ''}
                </div>
              </div>
              <div className="review-item-column">
                <div className="review-item-label">Status After</div>
                <div className="review-item-value">
                  {afterStatus ? ReviewUtil.getStatusTextFromReview(afterStatus) : ''}
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="review-item-column">
                <div className="review-item-label">Status Before</div>
                <div className="review-item-value">
                  {beforeStatus ? ReviewUtil.getStatusTextFromReview(beforeStatus) : 'N/A'}
                </div>
              </div>
              <div className="review-item-column">
                <div className="review-item-label">Status After</div>
                <div className="review-item-value">
                  {afterStatus ? ReviewUtil.getStatusTextFromReview(afterStatus) : 'N/A'}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {showReviewCommentHistory && item.info.message && (
        <div className="review-item-comment">
          <span className="review-item-label">Comment:</span>
          <span className="review-item-value">{item.info.message}</span>
        </div>
      )}
    </div>
  );
}

interface ReviewHistoryItemsListProps {
  reviewHistoryItems: Array<ReviewHistoryItem>;
  showReviewLifecycleColumns: boolean;
  showReviewCommentHistory: boolean;
  reviewProcessName?: string;
  reviewProcessId?: string | number;
}

function ReviewHistoryItemsList({
  reviewHistoryItems,
  showReviewLifecycleColumns,
  showReviewCommentHistory,
  reviewProcessName,
  reviewProcessId,
}: ReviewHistoryItemsListProps): JSX.Element | null {
  // Filter items if not showing comment history
  let filteredItems = reviewHistoryItems;
  if (!showReviewCommentHistory) {
    filteredItems = reviewHistoryItems.filter((item) => item.info.reviewAction !== ReviewAction.COMMENT);
  }

  if (filteredItems.length === 0) {
    return null;
  }

  // Use provided review process info or fallback to defaults
  const processName = reviewProcessName || 'Auditor-Based Review Process';
  const processId = reviewProcessId || '';

  return (
    <div className="review-history-actions-list">
      <h3 className="review-history-section-title">Review History</h3>
      <div className="review-process-info">
        <div className="review-process-info-row">
          <span className="review-process-label">Review Process Used:</span>
          <span className="review-process-value">{processName}</span>
        </div>
        <div className="review-process-info-row">
          <span className="review-process-label">Review Process ID:</span>
          <span className="review-process-value">{processId}</span>
        </div>
      </div>
      {filteredItems.map((item, index) => {
        // Calculate previous status for "Before" field
        const previousItem = index > 0 ? filteredItems[index - 1] : null;
        const beforeStatus = previousItem?.info.new_status;
        const afterStatus = item.info.new_status;
        const stageDefinition = item.reviewLifecycleInfo?.reviewStageInfo?.definition;

        // Get review stage information
        const reviewStage = item.reviewLifecycleInfo?.reviewStageInfo?.name || '';

        return (
          <ReviewHistoryItemComponent
            key={index}
            item={item}
            beforeStatus={beforeStatus}
            afterStatus={afterStatus}
            stageDefinition={stageDefinition}
            reviewStage={reviewStage}
            showReviewLifecycleColumns={showReviewLifecycleColumns}
            showReviewCommentHistory={showReviewCommentHistory}
          />
        );
      })}
    </div>
  );
}

function ReviewHistoryContainer({
  reviewHistoryItems,
  showReviewLifecycleColumns,
  showReviewCommentHistory,
  reviewProcessName,
  reviewProcessId,
}: ReviewHistoryContainerProps): JSX.Element {
  return (
    <div className="review-history-container">
      <div className="review-history-content">
        <ReviewHistoryItemsList
          reviewHistoryItems={reviewHistoryItems}
          showReviewLifecycleColumns={showReviewLifecycleColumns}
          showReviewCommentHistory={showReviewCommentHistory}
          reviewProcessName={reviewProcessName}
          reviewProcessId={reviewProcessId}
        />
      </div>
    </div>
  );
}

ReviewHistoryContainer.propTypes = {
  reviewHistoryItems: PropTypes.array.isRequired,
  showReviewLifecycleColumns: PropTypes.bool.isRequired,
  showReviewCommentHistory: PropTypes.bool.isRequired,
  reviewProcessName: PropTypes.string,
  reviewProcessId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ReviewHistoryContainer;
